import {Injectable} from "@angular/core";
import {TranslocoService} from "@ngneat/transloco";
import {ToastrService} from "ngx-toastr";
import {MatDialog} from "@angular/material/dialog";
import {CdkDragDrop, CdkDragMove} from "@angular/cdk/drag-drop";
import {Compbaser} from "../components/compbaser";
import {StoreSelectorsService} from "./store-selectors.service";
import {EnumWizardUpgradeToasts, Id, ILayoutConstraints, IPlayerProps, ISceneAddBlock, ISceneBlock, ISelectedSceneSceneId, IUser} from "../interfaces";
import {actionAddSceneItem, actionSelectedSceneItemId} from "../store/actions/user.actions";
import {ContainerService} from "./container.service";
import {ADDBLOCK_OFFSET, convertNumberToBoolean, DRAG_ITEM_SIZE, HEAD_FONT_RATE, isNullOrUndef, TITLE_FONT_RATE} from "../common";
import {IBlockLabelProp} from "../containers/editor/scene-blocks/block-constants";
import {mergeMap, take} from 'rxjs/operators';
import {isNotNullOrUndefined} from "codelyzer/util/isNotNullOrUndefined";
import {LocalStoreService} from "./local-store.service";
import {YoutubeLicenseDialogComponent} from "../containers/user/youtube-license-dialog/youtube-license-dialog.component";
import {ITimelineViewMode} from "../containers/editor/scene-editor/scene-editor.component";
import {timeout} from "../decorators/timeout";
import {of} from 'rxjs';
import {Lib} from "libs/Lib";
import * as _ from 'lodash';
import {AppService} from "./app.service";
import {Memoizee} from "../decorators/memoizee-decorator";

@Injectable()
export class SceneAddService extends Compbaser {
    sceneId: Id;
    sceneItemId: Id;
    players: ISceneBlock[] = [];
    studioId: string;
    sceneLayout: ILayoutConstraints;
    sceneMime: any;
    isResellerStatusActive: boolean;
    touchPlayerIndex = -1;
    sceneW = 0;
    sceneH = 0;
    addBlockType: ISceneAddBlock;
    activeBlockId;
    color = '';
    zoom: any;
    blockSmallView = false;
    user: IUser;

    constructor(
        private storeSelector: StoreSelectorsService,
        private translocoService: TranslocoService,
        private localStoreService: LocalStoreService,
        private container: ContainerService,
        private toastr: ToastrService,
        private appService: AppService,
        protected override dialog: MatDialog
    ) {
        super(dialog);
        this.cod(
            this.storeSelector.listenSelectedSceneIdAndSceneItemId()
                .subscribe((v: ISelectedSceneSceneId) => {
                    this.sceneId = v.sceneId;
                    this.sceneItemId = v.sceneItemId;
                })
        );
        this.cod(
            this.storeSelector.listenSelectedScene().subscribe((i_scene: IPlayerProps) => {
                if (i_scene && i_scene.Player) {
                    this.sceneMime = i_scene.Player.$.mimeType;
                    this.sceneLayout = i_scene.Player.Data[0].Layout[0].$;
                    this.sceneW = +this.sceneLayout.width;
                    this.sceneH = +this.sceneLayout.height;
                    this.players = (i_scene.Player.Data["0"]?.Scene["0"]?.Players["0"]?.Player || []) as ISceneBlock[];
                } else {
                    this.sceneMime = null;
                }
            })
        );
        this.cod(
            this.storeSelector.getPlayerIndexByTouch(this.sceneId).subscribe(v => {
                this.touchPlayerIndex = v;
            })
        );
        this.cod(
            this.storeSelector.getUser()
                .subscribe((i_user: IUser) => {
                    this.user = i_user;
                    this.isResellerStatusActive = Lib.IsResellerStatusActive(this.user);
                })
        );
        this.cod(
            this.container.zoom.subscribe(z => {
                this.zoom = z || 1;
            })
        );
    }

    setBlockType(i_type: ISceneAddBlock) {
        this.addBlockType = i_type;
    }

    setActiveBlockId(id: Id) {
        this.activeBlockId = id;
    }

    setColor(i_color) {
        this.color = i_color;
    }

    setBlockView(i_view) {
        this.blockSmallView = i_view;
    }

    private isMimeSet(): boolean {
        return !(this.sceneMime === '' || _.isUndefined(this.sceneMime));
    }

    private isDropAllowed(i_blockAdding): boolean {
        if (i_blockAdding.id === this.storeSelector.blockLabels.YOUTUBE.id) {
            const isYouTubeLicense = convertNumberToBoolean(this.localStoreService.getStorageValConfig('isYouTubeLicense'));
            if (!isYouTubeLicense) {
                this.youtubeLicense();
                return;
            }
        }
        if (Lib.IsAccessBlocked(this.user)) {
            this.toastr.error(this.translocoService.translate('account.screenDisruption', {}, this.translocoService.getActiveLang()), '[ Contact live chat ]');
            return false;
        }
        if (!Lib.IsBlockAddable(i_blockAdding, this.user)) {
            this.routeToUpgrade();
            return false;
        }
        // if ((i_blockAdding.enterprise && !this.enterpriseMode) || (i_blockAdding.enterprise && !this.isResellerStatusActive)) {
        //     this.routeToUpgrade();
        //     return false;
        // }
        return true;
    }

    private youtubeLicense() {
        this.dialog.open(YoutubeLicenseDialogComponent, {
            height: '500px',
            width: '650px'
        });
    }

    @timeout(333)
    private routeToUpgrade() {
        this.appService.openWizardUpgradeDialog(EnumWizardUpgradeToasts.UPGRADE_TO_USE_FEATURE);
        // this.route.navigateByUrl('/home');
        // this.route.navigateByUrl(`/user/enterprise`);
    }

    /** add component, multiple scenes and resources */
    addBlock(event = null, viewMode: ITimelineViewMode = null, autoSelect = false) {
        if (!event) event = {};
        if (viewMode === 'playlistMode') {
            const item = this.getBlockIdAndType(event);
            this.container.setDndContent(item, "playlist", false);
            return;
        }
        if (viewMode) return;

        const blocks = Array.isArray(event) ? event : [event];
        const addBlocks = [];
        blocks.forEach((_block) => {
            const blockAdding = this.generateBlockDataFromEvent(_block);
            if (!blockAdding) return;
            const t = _.cloneDeep(blockAdding.template);
            if (isNotNullOrUndefined(t.Data[0]?.Schedule?.[0]?.$?.id)) t.Data[0].Schedule[0].$.id = event.metadata;
            addBlocks.push(t);
        });
        const offset = this.storeSelector.addedBlockCount * ADDBLOCK_OFFSET;
        this.addScenePlayers(addBlocks, offset, offset, null, true, autoSelect);
    }

    /** drop component, scene and resource */
    dropBlock(event: CdkDragDrop<any>, droppedComponent = null, autoSelect = false) {
        if (droppedComponent === 'playlist') {
            const item = this.getBlockIdAndType(event);
            this.container.setDndContent(item, "playlist", true, false, event.dropPoint.x, event.dropPoint.y);
            return;
        }

        debugger;

        const blockAdding = this.generateBlockDataFromEvent(event);
        if (!blockAdding) return;
        const blockData: ISceneBlock = _.cloneDeep(blockAdding.template);
        if (isNotNullOrUndefined(blockData.Data[0]?.Schedule?.[0]?.$?.id)) blockData.Data[0].Schedule[0].$.id = event.item.data.metadata;
        const offset = this.getOffset(event);
        this.addScenePlayers([blockData], offset.offsetX, offset.offsetY, null, true, autoSelect);
    }

    dragBlock(event: CdkDragMove<any>, draggedComponent = null) {
        if (draggedComponent === 'playlist') {
            const item = this.getBlockIdAndType(event.source.data);
            this.container.setDndContent(item, "playlist", false, true, event.pointerPosition.x, event.pointerPosition.y);
            return;
        }
    }

    /** add multi-types labels */
    addHeaderLabel(labelType: 'head' | 'title' | 'label') {
        const labelWidth = this.sceneW / 2;
        let labelText, fontSize, fontFamily, labelY;

        switch (labelType) {
            case 'head': {
                fontSize = this.sceneW * HEAD_FONT_RATE;
                labelText = 'HEADER';
                fontFamily = 'Carbon Block';
                labelY = 50;
                break;
            }
            case 'title': {
                fontSize = this.sceneW * TITLE_FONT_RATE;
                labelText = 'TITLE';
                fontFamily = 'Carbon Block';
                labelY = 50 + this.sceneW * HEAD_FONT_RATE;
                break;
            }
            case 'label': {
                fontSize = 20;
                labelText = 'LABEL';
                labelY = 100 + this.sceneW * HEAD_FONT_RATE + this.sceneW * TITLE_FONT_RATE;
                break;
            }
            default: {
                break;
            }
        }

        if (!fontSize) return;
        const labelComponent = this.storeSelector.blockLabels.LABEL;
        const blockData: ISceneBlock = _.cloneDeep(labelComponent.template);

        // set layout
        const layout = blockData.Data['0'].Layout['0']?.$;
        layout.width = `${labelWidth}`;
        layout.height = `${fontSize}`;
        layout.x = `${Math.floor((this.sceneW - labelWidth) / 2)}`;
        layout.y = `${labelY}`;

        // set font
        const font = blockData.Data['0'].Label['0'];
        font.Font['0'].$.fontSize = fontSize;
        font.Text = [labelText];
        if (this.color) {
            font.Font['0'].$.fontColor = `${Lib.HexToDecimal(this.color)}`;
        }
        if (fontFamily) {
            font.Font['0'].$.fontFamily = fontFamily;
        }

        const offset = this.storeSelector.addedBlockCount * ADDBLOCK_OFFSET;
        this.addScenePlayers([blockData], offset, offset);
    }

    /** add the imported/dropped players to scene */
    addScenePlayers(blocks: ISceneBlock[], offsetX = 0, offsetY = 0, custom = null, isMultiBlocks = true, autoSelect = false) {
        debugger;
        if (!blocks || blocks.length === 0) return;
        const updatedItemIds = [];
        const values = [];

        blocks.forEach((_player, ind) => {
            const newId = _.uniqueId('__');
            updatedItemIds.push(newId);
            if (isMultiBlocks) this.storeSelector.addedBlockCount += 1;
            this.setBlockLayout(_player, offsetX, offsetY, newId, custom, isMultiBlocks ? ind : 0);

            if (this.color && this.addBlockType === 'BLOCK_SNIPPETS' &&
                _player.Data[0].Label && _player.Data[0].Label[0]
            ) {
                _player.Data[0].Label[0].Font[0].$.fontColor = `${Lib.HexToDecimal(this.color)}`;
            }

            values.push({
                key: 'Player',
                cloneValue: true,
                value: _player,
                storePath: 'Data["0"].Scene["0"].Players[0]',
                addIndex: this.touchPlayerIndex < 0 ? null : this.touchPlayerIndex
            });
        });

        this.storeSelector.dispatch(actionAddSceneItem({
            sceneId: this.sceneId,
            sceneItemId: this.sceneId,
            updatedItemIds,
            values
        }));

        if (autoSelect && updatedItemIds.length === 1) {
            // temporarily comment out this
            // this.container.activeSceneItemIds.next(updatedItemIds);
            // this.storeSelector.dispatch(actionSelectedSceneItemId({id: updatedItemIds[0]}));
        }
    }

    private generateBlockDataFromEvent(i_event): IBlockLabelProp {
        const {blockId, blockType} = this.getBlockIdAndType(i_event);
        debugger;
        if (isNullOrUndef(blockId) || !this.addBlockType) {
            return;
        }
        return this.generateBlockData(blockId, blockType);
    }

    public generateBlockData(blockId, blockType): IBlockLabelProp {
        let blockAdding: IBlockLabelProp;
        if (this.addBlockType !== 'BLOCK_COMPONENTS' && this.addBlockType !== 'BLOCK_SCHEDULER') {
            /******************************/
            /** adding scene or resource **/
            /******************************/
            blockAdding = this.storeSelector.getBlockLabelByFileType(blockType.toLowerCase());
            if (!blockAdding) return;
            if (!this.isDropAllowed(blockAdding)) return null;


            let w, h;
            if (blockAdding.template.$.player === this.storeSelector.blockLabels.BLOCKCODE_SCENE.id) {

                /** if adding Json based scene we wrap it in BlockJsonComponent else just add from the template **/
                this.cod(
                    this.storeSelector.getScene(blockId).pipe(take(1)).subscribe((s) => {
                        const block = this.storeSelector.getBlockLabelByMimeType(s.Player.$.mimeType, false);
                        const label = s.Player.$.label;
                        w = s.Player.Data['0'].Layout['0']?.$?.width;
                        h = s.Player.Data['0'].Layout['0']?.$?.height;

                        if (block && block.jsonPlayerBased) {
                            blockAdding = this.storeSelector.getComponentForBlock(s.Player.$.mimeType, this.sceneMime);
                            const blockTemplate = blockAdding.template;
                            blockTemplate.$.label = label;

                            switch (this.storeSelector.handleMimeAnomalies(blockTemplate)) {
                                case "STOCKS": {
                                    blockTemplate.Data[0].Stocks[0].Player[0].$.src = blockId;
                                    break;
                                }
                                case "XML": {
                                    blockTemplate.Data[0].Xml[0].Player[0].$.src = blockId;
                                    break;
                                }
                                case "CUSTOM_RSS": {
                                    blockTemplate.Data[0].Rss[0].CustomRss[0].Player[0].$.src = blockId;
                                    // if (blockTemplate.Data[0].Rss[0]) {
                                    //     blockTemplate.Data[0].Rss[0].CustomRss[0].Player[0].$.src = blockId;
                                    // } else {
                                    //     blockTemplate.Data[0].Rss[1].CustomRss[0].Player[0].$.src = blockId;
                                    // }
                                    break;
                                }
                                case "CATALOG": {
                                    blockTemplate.Data[0].Catalog[0].Player[0].$.src = blockId;
                                    break;
                                }
                                case "JSON": {
                                    blockTemplate.Data[0].Json[0].Player[0].$.src = blockId;
                                    break;
                                }
                            }
                        } else {
                            switch (s.Player.$.mimeType) {
                                /** deal with enhanced JsonPlayer types such as NetFeed 4300.1 **/
                                // case SceneMimeTypes.NetFeedNews:
                                // case SceneMimeTypes.NetFeedHoroscope: {
                                //     blockAdding = this.storeSelector.getComponentForBlock(s.Player.$.mimeType, this.sceneMime);
                                //     blockAdding.template.$.label = label;
                                //     blockAdding.template.Data[0].Json[0].Player[0].$.src = blockId;
                                //     break;
                                // }
                                /** standard scene **/
                                default: {
                                    blockAdding.template.$.src = blockId;
                                    blockAdding.template.$.label = label;
                                    if (this.addBlockType === 'BLOCK_STICKERS' || this.addBlockType === 'BLOCK_SNIPPETS') {
                                        blockAdding.template.$.private_child = true;
                                    }
                                }
                            }
                        }
                    })
                );
            } else {
                blockAdding.template.Data["0"].Resource["0"].$.resource = blockId;
                this.cod(
                    this.storeSelector.listenResourceById(blockId).subscribe((item_resource) => {
                        if (item_resource) {
                            w = item_resource.resource_pixel_width;
                            h = item_resource.resource_pixel_height;
                        }
                    })
                );
            }

            // Set width/height of adding block
            if (w && h) {
                const {width, height} = this.handleSize(+w, +h);
                blockAdding.template.Data[0].Layout['0'].$.width = width;
                blockAdding.template.Data[0].Layout['0'].$.height = height;
            }
            return blockAdding;
        } else {
            /**********************/
            /** adding component **/
            /**********************/
            blockAdding = this.storeSelector.getComponentForBlock(blockId, this.sceneMime);
            if (!this.isDropAllowed(blockAdding)) return null;
            if (blockAdding.template.$.player === this.storeSelector.blockLabels.JSONITEM.template.$.player) return blockAdding;


            // if component is json based, assign the first player when create.
            this.cod(
                this.storeSelector.getScenes(true).pipe(take(1)).subscribe((i_scenes) => {
                    const blockTemplate = blockAdding.template;
                    for (const scene of i_scenes) {
                        let assigned = false;
                        if (blockAdding.types.findIndex((v) => v === scene.Player.$.mimeType) > -1) {
                            const playerId = scene.Player.$.id;

                            switch (this.storeSelector.handleMimeAnomalies(blockTemplate)) {
                                case "STOCKS": {
                                    blockTemplate.Data[0].Stocks[0].Player[0].$.src = playerId;
                                    assigned = true;
                                    break;
                                }
                                case "XML": {
                                    blockTemplate.Data[0].Xml[0].Player[0].$.src = playerId;
                                    assigned = true;
                                    break;
                                }
                                case "CATALOG": {
                                    blockTemplate.Data[0].Catalog[0].Player[0].$.src = playerId;
                                    assigned = true;
                                    break;
                                }
                                case "CUSTOM_RSS": {
                                    blockTemplate.Data[0].Rss[0].CustomRss[0].Player[0].$.src = playerId;
                                    // if (blockTemplate.Data[0].Rss[0]?.CustomRss) {
                                    // } else {
                                    //     blockTemplate.Data[0].Rss[1].CustomRss[0].Player[0].$.src = playerId;
                                    // }
                                    assigned = true;
                                    break;
                                }
                                default: {
                                    if (blockTemplate.Data[0]?.Json?.[0]?.Player?.[0]) {
                                        blockTemplate.Data[0].Json[0].Player[0].$.src = playerId;
                                    }
                                    assigned = true;
                                    break;
                                }
                            }
                        }
                        if (assigned) break;
                    }
                })
            );
            return blockAdding;
        }
    }

    private getBlockIdAndType(i_event) {
        let blockType;
        let blockName;
        const blockId = this.activeBlockId ? this.activeBlockId : (i_event.id ? i_event.id : i_event.item?.data?.id);

        if (this.addBlockType === 'BLOCK_RESOURCES' || this.addBlockType === 'BLOCK_SCENES') {
            blockType = i_event.type ? i_event.type : i_event.item?.data?.type;
            if (i_event.item?.data?.resourceType) {
                blockType = i_event.item.data.resourceType;
            }
            blockName = i_event.name ? i_event.name : i_event.item?.data?.name;
        }
        if (!blockType) {
            blockType = 'scene';
        }
        this.activeBlockId = null;

        return {
            blockId,
            blockType,
            blockName
        };
    }

    private handleSize(w, h) {
        if (w && h && this.sceneH && this.sceneW) {
            const ratio = h / w;
            const percent = 0.25;
            if (w > this.sceneW * percent) {
                w = this.sceneW * percent;
                h = w * ratio;
            }
            if (h > this.sceneH * percent) {
                h = this.sceneH * percent;
                w = h / ratio;
            }
        }
        return {width: `${w}`, height: `${h}`};
    }

    private setBlockLayout(block: ISceneBlock, offsetX = 0, offsetY = 0, newId = null, custom = null, offsetCount = 0) {
        if (!block) return;

        const blockLayout = block.Data[0].Layout['0'].$;
        let x = +blockLayout.x;
        let y = +blockLayout.y;

        if (newId) block.$.id = newId;
        if (offsetX) x += offsetX;
        if (offsetY) y += offsetY;
        if (offsetCount) {
            x += ADDBLOCK_OFFSET * offsetCount;
            y += ADDBLOCK_OFFSET * offsetCount;
        }

        blockLayout.x = `${x}`;
        blockLayout.y = `${y}`;

        if (!blockLayout.width || !blockLayout.height || blockLayout.width == '0' || blockLayout.height == '0') {
            blockLayout.width = `${DRAG_ITEM_SIZE}`;
            blockLayout.height = `${DRAG_ITEM_SIZE}`;
        }

        if (custom) {
            Object.keys(custom).forEach((key) => {
                blockLayout[key] = custom[key];
            });
        }
    }

    getOffset(event: CdkDragDrop<any>) {
        if (!event || !document.querySelector(".view")) return {};

        const bounds = document.querySelector(".view").getBoundingClientRect();
        const offsetX = Math.round((event.dropPoint.x - bounds.left) / this.zoom);
        const offsetY = Math.round((event.dropPoint.y - bounds.top) / this.zoom);
        return {
            offsetX,
            offsetY
        };
    }

    /**
     * not using right now
     * good function that if enabled we can check mime restrictions
     */
    isMimeTypeRestricted(block: IBlockLabelProp) {
        if (!this.isMimeSet()) return 1;
        if (!this.storeSelector.isBlockIdJsonItemBased(block.id) && !this.storeSelector.isBlockIdJsonBased(block.id)) return 1;
        if (this.sceneMime === block.types?.[0]) return 1;
        return 0.2;
    }
}
